import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Building2, User, Search } from "lucide-react";
import { useSearchUsers } from "@/hooks/use-users";
import { useDebounce } from "@/hooks/use-debounce";

interface GlobalSearchCommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearchCommand({ open, onOpenChange }: GlobalSearchCommandProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  // Search users
  const { data: userResults = [] } = useSearchUsers(debouncedQuery);

  // TODO: Add school search when available
  // const { data: schoolResults = [] } = useSearchSchools(debouncedQuery);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  const handleSelectUser = (userId: number, schoolId: string | null) => {
    // General users have no school, so there is no school-scoped page to land on.
    // NOTE: the school-scoped path below targets a route that is not registered
    // in the router and currently falls through to NotFoundPage.
    navigate(
      schoolId
        ? `/dashboard/schools/${schoolId}/users?id=${userId}`
        : `/dashboard/users`,
    );
    onOpenChange(false);
    setQuery("");
  };

  const handleSelectSchool = (schoolId: string) => {
    navigate(`/dashboard/schools/${schoolId}`);
    onOpenChange(false);
    setQuery("");
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search users and schools..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {query.length < 4 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <Search className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Type at least 4 characters to search
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Press <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                  {typeof navigator !== 'undefined' && navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}K
                </kbd> to toggle
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6">
              <p className="text-sm text-muted-foreground">No results found</p>
            </div>
          )}
        </CommandEmpty>

        {query.length >= 4 && userResults.length > 0 && (
          <>
            <CommandGroup heading="Users">
              {userResults.map((user) => (
                <CommandItem
                  key={`user-${user.id}`}
                  value={`${user.id}-${user.firstName}-${user.lastName}-${user.email}`}
                  onSelect={() => handleSelectUser(user.id, user.schoolId)}
                  className="flex items-start gap-3 py-3"
                >
                  <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {user.firstName} {user.lastName}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ID: {user.id}
                      </span>
                    </div>
                    {user.email && (
                      <span className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </span>
                    )}
                    {user.phoneNumber && (
                      <span className="text-xs text-muted-foreground">
                        {user.phoneNumber}
                      </span>
                    )}
                    <Badge variant="outline" className="w-fit mt-1">
                      {user.schoolId}
                    </Badge>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* TODO: Add school search results when available
        {query.length >= 4 && schoolResults.length > 0 && (
          <CommandGroup heading="Schools">
            {schoolResults.map((school) => (
              <CommandItem
                key={`school-${school.id}`}
                value={school.name}
                onSelect={() => handleSelectSchool(school.id)}
                className="flex items-start gap-3 py-3"
              >
                <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="flex flex-col gap-1 flex-1">
                  <span className="font-medium">{school.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {school.id} • {school.state}
                  </span>
                  <Badge
                    variant={school.status === 'live' ? 'default' : 'secondary'}
                    className="w-fit mt-1"
                  >
                    {school.status}
                  </Badge>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        */}
      </CommandList>
    </CommandDialog>
  );
}

